import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { Classroom } from '@innovamat/glow-api-client';
import { useGetCurrentWeekPeriodQuery } from '@innovamat/glow-api-client';
import { dates } from '@innovamat/radiance-utils';

import { useOrganization } from '../../classrooms-management';
import { getTimezone } from '../../shared/modules/organization/organization';
import { is12hClock } from '../../shared/modules/region/region-parameters';

type Props = {
  classroom?: Classroom;
  digitalPracticeFirstDayOfWeek: string;
  digitalPracticeFirstHourOfWeek?: string;
  showHourDropdownPractice?: boolean;
};

type UseDigitalPracticeAnnouncementReturn = {
  currentWeekPeriod: {
    start: { date: string; timezone: string };
    end: { date: string; timezone: string };
  } | null;
  daysOfWeek: dates.DayOfWeek[];
  formatDate: (date: Date, timezone?: string) => string;
  formatTime: ({
    date,
    clock,
    timezone,
  }: {
    date?: Date;
    clock?: string;
    timezone?: string;
  }) => string;
  hasChanges: boolean;
  nextOccurrence: Date | null;
  timezone: string | undefined;
  isLoading: boolean;
};

const calculateDaysUntilNextWeekday = (
  currentDay: number,
  targetDay: number,
  currentHour: number,
  targetHour: number
): number => {
  let daysUntilTarget = targetDay - currentDay;

  if (daysUntilTarget === 0 && currentHour < targetHour) {
    daysUntilTarget = 0;
  } else if (daysUntilTarget <= 0) {
    daysUntilTarget += 7;
  }

  return daysUntilTarget;
};

function useDigitalPracticeAnnouncement({
  classroom,
  digitalPracticeFirstDayOfWeek,
  digitalPracticeFirstHourOfWeek = '00:00:00',
  showHourDropdownPractice = false,
}: Props): UseDigitalPracticeAnnouncementReturn {
  const { t } = useTranslation();

  const { organization } = useOrganization();
  const daysOfWeek = dates.getDaysOfWeek(t);

  const {
    data: currentWeekPeriod,
    isLoading: isLoadingWeekPeriod,
    isFetching,
  } = useGetCurrentWeekPeriodQuery(
    { classroomId: classroom?.id || '' },
    { enabled: !!classroom?.id }
  );

  const timezone = organization ? getTimezone(organization) : undefined;

  const isLoading = !classroom || isLoadingWeekPeriod || isFetching;

  const hasChanges =
    digitalPracticeFirstDayOfWeek !==
      (classroom?.digitalPracticeFirstDayOfWeek || '') ||
    digitalPracticeFirstHourOfWeek !==
      (classroom?.digitalPracticeFirstHourOfWeek || '');

  const getNextWeekdayOccurrence = (
    weekday: string | null | undefined,
    hour: string | null | undefined
  ): Date | null => {
    if (!weekday) return null;

    const targetDay = daysOfWeek.find(
      (day) => day.value === weekday
    )?.numericDay;
    if (targetDay === undefined) return null;

    const now = DateTime.now().setZone(timezone);
    const todayDay = now.weekday % 7;
    const currentHour = now.hour;

    const targetHour = hour ? parseInt(hour.split(':')[0], 10) : 0;

    const daysUntilNext = calculateDaysUntilNextWeekday(
      todayDay,
      targetDay,
      currentHour,
      targetHour
    );

    const nextOccurrence = now.plus({ days: daysUntilNext }).set({
      hour: targetHour,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    return nextOccurrence.toJSDate();
  };

  const formatDate = (date: Date, customTimezone?: string): string => {
    const dateTime = customTimezone
      ? DateTime.fromJSDate(date).setZone(customTimezone)
      : DateTime.fromJSDate(date);

    const weekdayNum = dateTime.weekday % 7;
    const translatedWeekday = daysOfWeek.find(
      (day) => day.numericDay === weekdayNum
    )?.label;
    const dayNumber = dateTime.day;
    return `${translatedWeekday} ${dayNumber}`;
  };

  const formatTime = ({
    date,
    clock,
    customTimezone,
  }: {
    date?: Date;
    clock?: string;
    customTimezone?: string;
  }): string => {
    const currentTimezone = customTimezone || timezone;

    if (!showHourDropdownPractice) {
      return dates.formatTime({
        dateTime: dates.DEFAULT_TIME,
        hour12: is12hClock(clock),
        timezone: currentTimezone,
      });
    }

    if (!date) {
      return dates.formatTime({
        dateTime:
          classroom?.digitalPracticeFirstHourOfWeek || dates.DEFAULT_TIME,
        hour12: is12hClock(clock),
        timezone: currentTimezone,
      });
    }

    return dates.formatTime({
      dateTime: date,
      hour12: is12hClock(clock),
      timezone: currentTimezone,
    });
  };

  const nextOccurrence = hasChanges
    ? getNextWeekdayOccurrence(
        digitalPracticeFirstDayOfWeek,
        digitalPracticeFirstHourOfWeek
      )
    : null;

  return {
    currentWeekPeriod: currentWeekPeriod?.getCurrentWeekPeriod || null,
    daysOfWeek,
    formatDate,
    formatTime,
    hasChanges,
    isLoading,
    nextOccurrence,
    timezone,
  };
}

export { useDigitalPracticeAnnouncement };
